import { BaseRepository } from './BaseRepository';

/**
 * Twilio Integration Config entity type
 */
export interface TwilioConfig {
  id: string;
  company_id: string;
  account_sid: string;
  auth_token_encrypted: string;
  main_phone_number: string;
  webhook_url: string;
  recording_enabled: boolean;
  transcription_enabled: boolean;
  transcription_provider: 'deepgram' | 'openai' | 'google';
  created_at: string;
  updated_at: string;
}

/**
 * Repository for managing Twilio integration configuration
 * Provides CRUD operations for Twilio settings (one per company)
 */
export class TwilioConfigRepository extends BaseRepository<TwilioConfig> {
  constructor() {
    super({ tableName: 'twilio_integration_config', primaryKey: 'id' });
  }

  /**
   * Find Twilio config for a specific company
   * Note: Each company should have only one Twilio config
   */
  async findByCompany(companyId: string): Promise<TwilioConfig | null> {
    const configs = await this.findAll({
      filters: { company_id: companyId },
      limit: 1
    });

    return configs.length > 0 ? configs[0] : null;
  }

  /**
   * Create or update Twilio config for a company
   * Ensures only one config exists per company
   */
  async upsert(companyId: string, data: Partial<TwilioConfig>): Promise<TwilioConfig> {
    const existing = await this.findByCompany(companyId);

    if (existing) {
      return this.update(existing.id, data);
    } else {
      return this.create({
        ...data,
        company_id: companyId
      });
    }
  }

  /**
   * Update auth token (encrypted)
   */
  async updateAuthToken(
    companyId: string, 
    encryptedToken: string
  ): Promise<TwilioConfig> {
    const config = await this.findByCompany(companyId);
    if (!config) {
      throw new Error('Twilio config not found for company');
    }

    return this.update(config.id, { 
      auth_token_encrypted: encryptedToken 
    });
  }

  /**
   * Toggle recording enabled
   */
  async toggleRecording(companyId: string): Promise<TwilioConfig> {
    const config = await this.findByCompany(companyId);
    if (!config) {
      throw new Error('Twilio config not found for company');
    }

    return this.update(config.id, { 
      recording_enabled: !config.recording_enabled 
    });
  }

  /**
   * Toggle transcription enabled
   */
  async toggleTranscription(companyId: string): Promise<TwilioConfig> {
    const config = await this.findByCompany(companyId);
    if (!config) {
      throw new Error('Twilio config not found for company');
    }

    return this.update(config.id, { 
      transcription_enabled: !config.transcription_enabled 
    });
  }

  /**
   * Update transcription provider
   */
  async updateTranscriptionProvider(
    companyId: string,
    provider: TwilioConfig['transcription_provider']
  ): Promise<TwilioConfig> {
    const config = await this.findByCompany(companyId);
    if (!config) {
      throw new Error('Twilio config not found for company');
    }

    return this.update(config.id, { transcription_provider: provider });
  }

  /**
   * Update phone number
   */
  async updatePhoneNumber(
    companyId: string,
    phoneNumber: string
  ): Promise<TwilioConfig> {
    const config = await this.findByCompany(companyId);
    if (!config) {
      throw new Error('Twilio config not found for company');
    }

    return this.update(config.id, { main_phone_number: phoneNumber });
  }
}

// Export singleton instance
export const twilioConfigRepository = new TwilioConfigRepository();
